// What the results panel says when a practice ends. Pure: the toasts and the animation are the
// component's business, everything that decides the wording is here.

import { verdictGlyph, VERDICTS } from "./moveRating.ts";
import type { Verdict } from "./moveRating.ts";

export interface PracticeOutcome {
    title: string;
    counted: boolean;
    isRetry: boolean;
    ownMoves: number;
    accuracy: number | null;
    stars: number;
    counts: Partial<Record<Verdict, number>>;
    moveXp: number;
    xp: number;
    bonuses: { code: string; label: string; xp: number }[];
    practiceNumber: number;
    isPersonalBest?: boolean;
    previousBest?: number | null;
    rewards?: { beltUp?: { name: string } | null; achievements?: { name: string; desc: string }[] } | null;
    slip?: { label: string; bestSan?: string | null; ply: number } | null;
}

export interface XpLine {
    label: string;
    xp: number;
    total?: boolean;
}

export interface ResultView {
    accuracy: string;
    stars: string;
    starsLabel: string;
    counts: { code: Verdict; glyph: string; n: number }[];
    xpLines: XpLine[];
    notes: string[];
    slip: { label: string; bestSan: string | null; ply: number } | null;
    canChallenge: boolean;
}

const starString = (count: number) => "★".repeat(count) + "☆".repeat(3 - count);

export function resultView(result: PracticeOutcome, { isLoggedIn, lineSource }: {
    isLoggedIn: boolean;
    lineSource: string | null;
}): ResultView {
    const notes: string[] = [];
    if (result.isPersonalBest) {
        notes.push(`New personal best, up from ${Math.round(result.previousBest ?? 0)}%.`);
    } else if (result.previousBest !== null && result.previousBest !== undefined && result.accuracy !== null) {
        notes.push(`Your best on this line is ${Math.round(result.previousBest)}%.`);
    }
    if (result.rewards?.beltUp) notes.push(`You earned the ${result.rewards.beltUp.name}.`);
    if (result.counted) {
        notes.push(isLoggedIn
            ? `Practice ${result.practiceNumber} saved to your account.`
            : `Practice ${result.practiceNumber} saved on this device.`);
    }

    const xpLines: XpLine[] = result.counted
        ? [{ label: "Moves", xp: result.moveXp },
           ...result.bonuses.map((b) => ({ label: b.label, xp: b.xp })),
           { label: "Total", xp: result.xp, total: true }]
        : [];

    return {
        accuracy: result.accuracy === null ? "–" : `${Math.round(result.accuracy)}%`,
        stars: starString(result.stars),
        starsLabel: `${result.stars} of 3 stars`,
        counts: VERDICTS
            .filter((code) => result.counts[code])
            .map((code) => ({ code, glyph: verdictGlyph(code), n: result.counts[code]! })),
        xpLines,
        notes,
        slip: result.slip ? { label: result.slip.label, bestSan: result.slip.bestSan ?? null, ply: result.slip.ply } : null,
        // Worth showing off, and on a line that has a page of its own to send people to.
        canChallenge: lineSource === "catalog" && result.counted && result.accuracy !== null,
    };
}

/** The one notification shown for however many achievements were unlocked at once. */
export function achievementToast(unlocked: { name: string; desc: string }[]):
        { title: string; message: string } | null {
    if (!unlocked.length) return null;
    if (unlocked.length === 1) {
        return { title: `Achievement: ${unlocked[0]!.name}`, message: unlocked[0]!.desc };
    }
    return {
        title: `${unlocked.length} achievements unlocked`,
        message: unlocked.map((a) => a.name).join(", "),
    };
}
